import { useState, useEffect } from 'react';
import { SHOW_ELECTION_RESULTS } from '../config/features';

export interface Candidate {
  name: string;
  party: string;
  votes: number;
  status: 'Leading' | 'Won' | 'Trailing' | 'Lost' | string;
  image?: string;
}

export interface ConstituencyData {
  constituencyId: number;
  candidates: Candidate[];
  lastUpdated: number;
}

let cachedDataset: any[] | null = null;
let datasetPromise: Promise<any[]> | null = null;

const loadDataset = async (): Promise<any[]> => {
  if (cachedDataset) return cachedDataset;
  if (datasetPromise) return datasetPromise;

  datasetPromise = fetch('/data/election/tamil_nadu_2026_results.json')
    .then(r => {
      if (!r.ok) throw new Error('Failed to fetch data');
      return r.json();
    })
    .then(data => {
      cachedDataset = data;
      return data;
    })
    .catch(e => {
      datasetPromise = null;
      throw e;
    });

  return datasetPromise;
};

export const useConstituencyDetail = (constituencyId: number | string | null | undefined) => {
  const [data, setData] = useState<ConstituencyData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!constituencyId || !SHOW_ELECTION_RESULTS) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const fullData = await loadDataset();
        
        const targetNum = Number(constituencyId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const match = fullData.find((c: any) => c.constituency_number === targetNum);
        
        if (!match) {
          throw new Error(`Constituency #${targetNum} not found in dataset.`);
        }

        // Format raw json structure into component shape
        const result: ConstituencyData = {
          constituencyId: targetNum,
          lastUpdated: Date.now(),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          candidates: (match.candidates || []).map((c: any) => ({
            name: c.candidate_name,
            party: c.party,
            votes: c.votes || 0,
            status: c.status.toLowerCase() === 'won' ? 'Won' : 'Lost',
            image: c.image
          })).sort((a: any, b: any) => b.votes - a.votes)
        };

        if (isMounted) {
          setData(result);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Constituency details load err:', err);
          setError('Error loading result');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [constituencyId]);

  return { data, loading, error };
};
